import { useEffect, useRef } from 'react';

import { IRefreshEnum } from '~/enums/refresh.enum';
import useAuthStore from '~/stores/auth/useAuthStore';
import { useTokenStore } from '~/stores/token/useTokenStore';

export type IAuthHeartbeatOptions = {
  checkEveryMs?: number;
  idleAfterMs?: number;
  pathname?: string;
  isProtectedPath?: (pathname: string) => boolean;
};

export function useAuthHeartbeat(options: IAuthHeartbeatOptions = {}): void {
  const checkEveryMs = options.checkEveryMs ?? IRefreshEnum.checkEveryMs;
  const idleAfterMs = options.idleAfterMs ?? IRefreshEnum.idleAfterMs;
  const pathname = options.pathname ?? '';

  const isProtectedRoute = options.isProtectedPath?.(pathname) ?? false;

  const lastActivityAtRef = useRef<number>(Date.now());
  const runningRef = useRef(false);
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;

    const now = () => Date.now();
    const sinceLastActivityMs = () => now() - lastActivityAtRef.current;
    const isIdle = () => sinceLastActivityMs() > idleAfterMs;

    const markActivity = () => {
      lastActivityAtRef.current = now();
    };

    const tick = async () => {
      if (!isProtectedRoute) return;
      if (disposedRef.current) return;
      if (runningRef.current) return;
      if (!useTokenStore.getAccess()) return;
      if (isIdle()) return;

      runningRef.current = true;

      try {
        await useAuthStore.getState().authTokenSlice.ensureValidToken();
      } catch {
        useAuthStore.getState().authTokenSlice.logoutLocal();
      } finally {
        runningRef.current = false;
      }
    };

    const passiveOpts: AddEventListenerOptions = { passive: true };

    window.addEventListener('scroll', markActivity, passiveOpts);
    window.addEventListener('wheel', markActivity, passiveOpts);
    window.addEventListener('keydown', markActivity);
    window.addEventListener('click', markActivity);
    window.addEventListener('focus', markActivity);

    void tick();

    const id = window.setInterval(() => void tick(), checkEveryMs);

    return () => {
      disposedRef.current = true;
      window.clearInterval(id);

      window.removeEventListener('scroll', markActivity, passiveOpts);
      window.removeEventListener('wheel', markActivity, passiveOpts);
      window.removeEventListener('keydown', markActivity);
      window.removeEventListener('click', markActivity);
      window.removeEventListener('focus', markActivity);
    };
  }, [checkEveryMs, idleAfterMs, isProtectedRoute]);
}
